"use client"

import { useState, useEffect, useCallback } from "react"
import Image from "next/image"
import { useTranslations } from "next-intl"
import type { components } from "umbraco"
import { getMediaUrl } from "umbraco"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import { replaceRichTextLinks } from "utils"
import Container from "../../Container"
import ChevronBullet from "../../icons/ChevronBullet"
import ShareLink from "../../icons/ShareLink"
import ShareFacebook from "../../icons/ShareFacebook"
import ShareX from "../../icons/ShareX"
import ShareLinkedIn from "../../icons/ShareLinkedIn"
import ShareWhatsApp from "../../icons/ShareWhatsApp"
import {
  Root,
  ArticleContent,
  TitleMetaContainer,
  JobTitle,
  MetadataMobileStack,
  MetadataMobileRow,
  MetadataMobileDivider,
  MetadataDesktopRow,
  LocationTag,
  LocationLabel,
  LocationValue,
  DateText,
  MetadataDivider,
  SocialShareRow,
  ShareButton,
  HeroImageWrapper,
  HeroImageDesktop,
  HeroImageMobile,
  ContentSection,
  SectionHeading,
  RichTextWrapper,
  BulletSection,
  BulletList,
  BulletItem,
  BulletIconWrapper,
  BulletText,
} from "./styles"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type CareerDetailData = components["schemas"]["CareerDetailPropertiesModel"]

interface Props {
  data: CareerDetailData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

// ---------------------------------------------------------------------------
// Translation map — English CMS dropdown values → locale display (EGDE-45)
// ---------------------------------------------------------------------------
const REGION_LABELS: Record<string, Record<string, string>> = {
  he: {
    "Gush Dan & Center": "גוש דן ומרכז",
    Shfela: "שפלה",
    South: "דרום",
    North: "צפון",
    "Jerusalem Area": "אזור ירושלים",
  },
  ar: {
    "Gush Dan & Center": "غوش دان والمركز",
    Shfela: "الشفلة",
    South: "الجنوب",
    North: "الشمال",
    "Jerusalem Area": "منطقة القدس",
  },
  ru: {
    "Gush Dan & Center": "Гуш-Дан и Центр",
    Shfela: "Шфела",
    South: "Юг",
    North: "Север",
    "Jerusalem Area": "Район Иерусалима",
  },
}

function translateRegion(
  value: string | null | undefined,
  locale: string,
): string | null {
  if (!value) return null
  if (locale === "en") return value
  const lang = locale.split("-")[0]
  return REGION_LABELS[lang]?.[value] ?? value
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Format a date string (ISO 8601 / YYYY-MM-DD) to Hebrew locale display.
 * Returns DD.MM.YY style matching the MagazineArticle pattern.
 */
function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return ""
  try {
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return dateStr
    return date.toLocaleDateString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    })
  } catch {
    return dateStr
  }
}

function getResponsiveImageUrl(blockList: unknown) {
  const list = blockList as
    | { items?: Array<{ content: { properties: Record<string, unknown> } }> }
    | null
    | undefined
  const item = list?.items?.[0]
  const props = item?.content?.properties
  if (!props) return { desktop: null, mobile: null, alt: "" }

  const desktopRel = getMediaUrl(props.desktopImage)
  const mobileRel = getMediaUrl(props.mobileImage)

  return {
    desktop: desktopRel ? getUmbracoMediaUrl(desktopRel) : null,
    mobile: mobileRel
      ? getUmbracoMediaUrl(mobileRel)
      : desktopRel
        ? getUmbracoMediaUrl(desktopRel)
        : null,
    alt: (props.altText as string) ?? "",
  }
}

/**
 * Split rich text HTML into an ordered sequence of list / non-list segments.
 *
 * EGDE-533: the previous parser extracted ONLY the <li> contents, so any
 * heading or paragraph the editor authored between two lists (e.g. the bold
 * "כישורים נדרשים" divider in Role Requirements) silently disappeared from
 * the page. Keeping the segments in document order preserves that content
 * while still letting the list items render with the styled chevron bullets.
 */
type RichTextSegment =
  | { type: "html"; html: string }
  | { type: "list"; items: string[] }

function parseRichTextSegments(markup: string): RichTextSegment[] {
  const segments: RichTextSegment[] = []
  // EGDE-539: only unordered lists become chevron-bullet rows. Ordered lists
  // stay in the html segments so their numbering survives (RichTextWrapper
  // styles the <ol> markers).
  const listRegex = /<ul[^>]*>([\s\S]*?)<\/ul>/gi
  const liRegex = /<li[^>]*>([\s\S]*?)<\/li>/gi
  // Meaningful = something remains after stripping tags, entities,
  // whitespace and punctuation. Editors fill mandatory RTE fields with a
  // lone "." to skip them — punctuation-only content must keep the whole
  // section hidden, as the old list-only parser did.
  const hasText = (html: string) =>
    html
      .replace(/<[^>]*>/g, "")
      .replace(/&[a-z#0-9]+;/gi, " ")
      .replace(/[\s.,;:!?"'()[\]{}\-–—_/\\|·•*+=~^%$#@&<>]+/g, "").length > 0

  let pendingItems: string[] = []
  const flushList = () => {
    if (pendingItems.length > 0) {
      segments.push({ type: "list", items: pendingItems })
      pendingItems = []
    }
  }

  let lastIndex = 0
  let match = listRegex.exec(markup)
  while (match) {
    const before = markup.slice(lastIndex, match.index)
    if (hasText(before)) segments.push({ type: "html", html: before })

    liRegex.lastIndex = 0
    let li = liRegex.exec(match[1])
    while (li) {
      const content = li[1]
      // Editors sometimes nest a divider heading INSIDE a bullet (the RTE
      // produces <li><p>…</p><h3>היקף המשרה</h3></li>). Hoist it out so it
      // renders as a standalone heading between two bullet groups — aligned
      // with the other headings instead of indented inside the bullet text.
      const heading = /<h[1-6][^>]*>/i.exec(content)
      if (heading) {
        const bulletPart = content.slice(0, heading.index)
        if (hasText(bulletPart)) pendingItems.push(bulletPart.trim())
        flushList()
        const hoisted = content.slice(heading.index)
        if (hasText(hoisted)) segments.push({ type: "html", html: hoisted })
      } else if (hasText(content)) {
        // Skip empty bullets (a trailing empty <li> is a common RTE leftover).
        pendingItems.push(content.trim())
      }
      li = liRegex.exec(match[1])
    }
    flushList()

    lastIndex = listRegex.lastIndex
    match = listRegex.exec(markup)
  }
  const after = markup.slice(lastIndex)
  if (hasText(after)) segments.push({ type: "html", html: after })
  return segments
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Social share icons.
 * RTL: source [WhatsApp, LinkedIn, X, Facebook, Link]
 *   => visual L→R: [Link, Facebook, X, LinkedIn, WhatsApp] — matches Figma.
 */
function SocialShareIcons({ title }: { title: string | null | undefined }) {
  const [pageUrl, setPageUrl] = useState("")

  useEffect(() => {
    setPageUrl(encodeURIComponent(window.location.href))
  }, [])

  const handleCopyLink = useCallback(() => {
    navigator.clipboard.writeText(window.location.href).catch(() => {})
  }, [])

  const pageTitle = encodeURIComponent(title ?? "")

  return (
    <SocialShareRow>
      <ShareButton
        href={`https://api.whatsapp.com/send?text=${pageTitle}%20${pageUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on WhatsApp"
      >
        <ShareWhatsApp />
      </ShareButton>
      <ShareButton
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${pageUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on LinkedIn"
      >
        <ShareLinkedIn />
      </ShareButton>
      <ShareButton
        href={`https://twitter.com/intent/tweet?url=${pageUrl}&text=${pageTitle}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on X"
      >
        <ShareX />
      </ShareButton>
      <ShareButton
        href={`https://www.facebook.com/sharer/sharer.php?u=${pageUrl}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Share on Facebook"
      >
        <ShareFacebook />
      </ShareButton>
      <ShareButton
        as="button"
        onClick={handleCopyLink}
        aria-label="Copy link"
        style={{ border: "none", background: "none", padding: 0 }}
      >
        <ShareLink />
      </ShareButton>
    </SocialShareRow>
  )
}

/**
 * Metadata bar — desktop horizontal, mobile stacked.
 *
 * RTL rule: source [A, B] => visual [B LEFT, A RIGHT].
 * Figma visual L→R: [social LEFT, divider, date, divider, location RIGHT]
 * => source must be reversed: [location, divider, date, divider, social]
 */
function MetadataBarSection({
  location,
  lastUpdated,
  title,
}: {
  location?: string | null
  lastUpdated?: string | null
  title?: string | null
}) {
  // EGDE-634: these two labels were hardcoded Hebrew, so /en served "מיקום:" and
  // "עודכן ב-" next to English content.
  const t = useTranslations("InnerPages")
  const updatedOn = lastUpdated
    ? t("jobUpdatedOn", { date: formatDate(lastUpdated) })
    : ""

  return (
    <>
      {/* Mobile: location + date on one row, social icons below */}
      <MetadataMobileStack>
        <MetadataMobileRow>
          {location && (
            <LocationTag>
              <LocationLabel variant="body1">{t("jobLocation")}</LocationLabel>
              <LocationValue variant="body1">{location}</LocationValue>
            </LocationTag>
          )}
          {location && lastUpdated && <MetadataMobileDivider />}
          {lastUpdated && <DateText variant="body2">{updatedOn}</DateText>}
        </MetadataMobileRow>
        <SocialShareIcons title={title} />
      </MetadataMobileStack>

      {/* Desktop: horizontal row — source reversed for RTL */}
      <MetadataDesktopRow>
        {location && (
          <LocationTag>
            <LocationLabel variant="body1">{t("jobLocation")}</LocationLabel>
            <LocationValue variant="body1">{location}</LocationValue>
          </LocationTag>
        )}
        <MetadataDivider />
        {lastUpdated && <DateText variant="body2">{updatedOn}</DateText>}
        <MetadataDivider />
        <SocialShareIcons title={title} />
      </MetadataDesktopRow>
    </>
  )
}

/**
 * Rich text renderer shared by all three career sections (EGDE-533/534):
 * non-list content (paragraphs, headings) renders as regular rich text in
 * document order, and every bullet list renders with the styled orange
 * chevron bullets — so the Job Description lists match the Requirements
 * lists instead of falling back to the browser's default markers.
 */
function RichTextSegments({
  richText,
}: {
  richText: { markup?: string } | null | undefined
}) {
  if (!richText?.markup) return null
  const segments = parseRichTextSegments(replaceRichTextLinks(richText.markup))
  if (segments.length === 0) return null

  return (
    <RichTextWrapper>
      {segments.map((segment, segmentIndex) =>
        segment.type === "html" ? (
          <div
            key={segmentIndex}
            dangerouslySetInnerHTML={{ __html: segment.html }}
          />
        ) : (
          <BulletList key={segmentIndex}>
            {segment.items.map((item, index) => (
              <BulletItem key={index}>
                {/* EGDE-325: icon first in source so the chevron sits at the
                    inline-start (right in RTL / left in LTR) per Figma. With
                    [text, icon] order the flex row placed the icon on the left
                    in RTL — the reported bug. */}
                <BulletIconWrapper>
                  <ChevronBullet />
                </BulletIconWrapper>
                <BulletText dangerouslySetInnerHTML={{ __html: item }} />
              </BulletItem>
            ))}
          </BulletList>
        ),
      )}
    </RichTextWrapper>
  )
}

/**
 * Section heading + chevron-bulleted rich text.
 */
function BulletListSection({
  heading,
  richText,
}: {
  heading: string
  richText: { markup?: string } | null | undefined
}) {
  if (!richText?.markup) return null
  if (parseRichTextSegments(richText.markup).length === 0) return null

  return (
    <BulletSection>
      <SectionHeading variant="h3">{heading}</SectionHeading>
      <RichTextSegments richText={richText} />
    </BulletSection>
  )
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function CareerDetail({ data, locale }: Props) {
  // EGDE-634: the three section headings were hardcoded Hebrew — /en showed
  // "תיאור משרה" / "תנאים סוציאליים" / "דרישות תפקיד" over English body copy.
  const t = useTranslations("InnerPages")
  const heroImage = getResponsiveImageUrl(data.heroImage)

  // location and category are now Dropdown data types which return string[]
  // from the Delivery API; extract first item if array.
  const rawLocation = Array.isArray(data.location)
    ? (data.location[0] ?? null)
    : data.location
  const location = translateRegion(rawLocation, locale)

  return (
    <>
      <Container paddingMobile>
        <Root>
          <ArticleContent>
            {/* Job title + metadata */}
            <TitleMetaContainer>
              <JobTitle variant="h1" component="h2">{data.jobTitle}</JobTitle>

              <MetadataBarSection
                location={location}
                lastUpdated={data.lastUpdated}
                title={data.jobTitle}
              />
            </TitleMetaContainer>

            {/* Hero image — EGDE-332: mobile gets its own crop (falls back to
                desktop), toggled by breakpoint instead of always desktop. */}
            {heroImage.desktop && (
              <HeroImageWrapper>
                <HeroImageDesktop>
                  <Image
                    src={heroImage.desktop}
                    alt={heroImage.alt || data.jobTitle || ""}
                    fill
                    sizes="800px"
                    style={{ objectFit: "cover" }}
                    priority
                  />
                </HeroImageDesktop>
                <HeroImageMobile>
                  <Image
                    src={heroImage.mobile ?? heroImage.desktop}
                    alt={heroImage.alt || data.jobTitle || ""}
                    fill
                    sizes="100vw"
                    style={{ objectFit: "cover" }}
                    priority
                  />
                </HeroImageMobile>
              </HeroImageWrapper>
            )}
          </ArticleContent>

          {/* Job description section — EGDE-534: rendered through the same
              segment renderer as the requirement sections so its bullet lists
              get the styled chevron bullets instead of the browser default. */}
          {data.jobDescription?.markup && (
            <ContentSection>
              <SectionHeading variant="h3">
                {t("jobDescriptionHeading")}
              </SectionHeading>
              <RichTextSegments richText={data.jobDescription} />
            </ContentSection>
          )}

          {/* Social conditions (bullet list) */}
          <BulletListSection
            heading={t("jobSocialConditionsHeading")}
            richText={data.socialConditions}
          />

          {/* Role requirements (bullet list) */}
          <BulletListSection
            heading={t("jobRequirementsHeading")}
            richText={data.roleRequirements}
          />
        </Root>
      </Container>
    </>
  )
}
